import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import { DeleteOutlined, EditOutlined, LinkOutlined, PlusOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { commerceServerTablePagination, COMMERCE_DEFAULT_PAGE_SIZE } from '../constants/commerceListPagination';
import { getUserPrimaryCompanyId, hasRole } from '../utils/permissionUtils';
import { COMMERCE_NAME_LOCALE_EXTRA_KEYS, parseNameLocalesField, resolveLocalizedEntityName } from '../constants/commerceNameLocales';
import { buildCommerceAuditColumns, useCommerceUserTimezone } from '../utils/commerceListDateUtils';

const { Title, Text } = Typography;

const CommerceCollectionsPage = () => {
  const { t, currentLanguage } = useLanguage();
  const userTimezoneOffset = useCommerceUserTimezone();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [collections, setCollections] = useState([]);
  const [collectionsTotal, setCollectionsTotal] = useState(0);
  const [collectionsPage, setCollectionsPage] = useState(1);
  const [collectionsPageSize, setCollectionsPageSize] = useState(COMMERCE_DEFAULT_PAGE_SIZE);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [linkedProductIds, setLinkedProductIds] = useState([]);
  const [linkedProducts, setLinkedProducts] = useState([]);
  const [syncingId, setSyncingId] = useState(null);
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [catalogOptionsLoading, setCatalogOptionsLoading] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(() => getUserPrimaryCompanyId());
  const [companiesForSelect, setCompaniesForSelect] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [linkProductSearchKeyword, setLinkProductSearchKeyword] = useState('');
  const [linkSearchDebounced, setLinkSearchDebounced] = useState('');
  const [linkModalProducts, setLinkModalProducts] = useState([]);
  const [linkModalTotal, setLinkModalTotal] = useState(0);
  const [linkModalPage, setLinkModalPage] = useState(1);
  const [linkModalPageSize, setLinkModalPageSize] = useState(COMMERCE_DEFAULT_PAGE_SIZE);
  const [linkModalLoading, setLinkModalLoading] = useState(false);
  const linkModalLastSearchFetched = useRef(null);
  const [form] = Form.useForm();

  const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('userInfo') || '{}') : {};
  const isTenantAdmin = hasRole(userInfo, 'Tenant_Admin');

  const fetchWithAuth = useCallback((url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(url, { ...options, headers });
  }, []);

  const loadCatalogOptions = useCallback(async () => {
    setCatalogOptionsLoading(true);
    try {
      const res = await fetchWithAuth('/api/commerce/catalogs');
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setCatalogOptions(list.map((c) => ({ label: c.name || c.id, value: c.id })));
    } catch (err) {
      console.warn('載入 Meta 目錄列表失敗:', err);
      setCatalogOptions([]);
    } finally {
      setCatalogOptionsLoading(false);
    }
  }, [fetchWithAuth]);

  const loadCompaniesForSelect = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/commerce/companies-for-select');
      if (!res.ok) return;
      const data = await res.json();
      const list = (Array.isArray(data) ? data : []).map(c => ({
        id: c.id ?? c.Id,
        name: (c.name ?? c.Name ?? '').trim() || (c.id ?? c.Id)
      }));
      setCompaniesForSelect(list);
    } catch (err) {
      console.warn('載入公司列表失敗:', err);
    }
  }, [fetchWithAuth]);

  const loadCollections = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (companyFilter) params.set('companyId', companyFilter);
      params.set('page', String(collectionsPage));
      params.set('pageSize', String(collectionsPageSize));
      const cRes = await fetchWithAuth(`/api/commerce/collections?${params.toString()}`);
      if (!cRes.ok) throw new Error(await cRes.text());
      const data = await cRes.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      const total = typeof data?.total === 'number' ? data.total : items.length;
      setCollections(items);
      setCollectionsTotal(total);
    } catch (err) {
      message.error(`${t('commerceCollections.loadFailed')}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, companyFilter, collectionsPage, collectionsPageSize, t]);

  const loadLinkModalProducts = useCallback(async () => {
    if (!selectedCollection || !linkModalOpen) return;
    setLinkModalLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(linkModalPage));
      params.set('pageSize', String(linkModalPageSize));
      if (linkSearchDebounced.trim()) params.set('search', linkSearchDebounced.trim());
      const cid = selectedCollection.companyId;
      if (isTenantAdmin && cid) params.set('companyId', String(cid));
      const pnid = selectedCollection.phoneNumberId;
      if (pnid) params.set('phoneNumberId', String(pnid));
      linkedProductIds.forEach((id) => params.append('excludeProductIds', id));
      const res = await fetchWithAuth(`/api/commerce/products?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      const total = typeof data?.total === 'number' ? data.total : items.length;
      setLinkModalProducts(items);
      setLinkModalTotal(total);
    } catch (err) {
      message.error(`${t('commerceCollections.loadFailed')}: ${err.message}`);
      setLinkModalProducts([]);
      setLinkModalTotal(0);
    } finally {
      setLinkModalLoading(false);
    }
  }, [
    fetchWithAuth,
    isTenantAdmin,
    linkModalOpen,
    linkModalPage,
    linkModalPageSize,
    linkSearchDebounced,
    linkedProductIds,
    selectedCollection,
    t
  ]);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  useEffect(() => {
    const t = setTimeout(() => setLinkSearchDebounced(linkProductSearchKeyword.trim()), 400);
    return () => clearTimeout(t);
  }, [linkProductSearchKeyword]);

  useEffect(() => {
    if (!linkModalOpen || !selectedCollection) return;
    if (linkModalLastSearchFetched.current !== linkSearchDebounced) {
      linkModalLastSearchFetched.current = linkSearchDebounced;
      if (linkModalPage !== 1) {
        setLinkModalPage(1);
        return;
      }
    }
    loadLinkModalProducts();
  }, [linkModalOpen, selectedCollection?.id, linkModalPage, linkModalPageSize, linkSearchDebounced, linkedProductIds, loadLinkModalProducts]);

  useEffect(() => {
    loadCompaniesForSelect();
  }, [loadCompaniesForSelect]);

  const openCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    if (isTenantAdmin && userInfo.company_id)
      form.setFieldsValue({ companyId: userInfo.company_id });
    loadCatalogOptions();
    loadCompaniesForSelect();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    const nl = parseNameLocalesField(record.nameLocales);
    form.setFieldsValue({
      companyId: record.companyId,
      name: record.name,
      nameLocales: {
        'zh-TC': nl['zh-TC'] || '',
        'zh-SC': nl['zh-SC'] || ''
      },
      description: record.description,
      externalCollectionKey: record.externalCollectionKey,
      metaProductSetId: record.metaProductSetId,
      metaCatalogId: record.metaCatalogId
    });
    loadCatalogOptions();
    loadCompaniesForSelect();
    setModalOpen(true);
  };

  // WABA 僅能連結一個目錄，表單預設使用已連結目錄
  useEffect(() => {
    if (modalOpen && catalogOptions.length === 1 && !editingRecord) {
      form.setFieldsValue({ metaCatalogId: catalogOptions[0].value });
    }
  }, [modalOpen, catalogOptions, editingRecord, form]);

  const saveCollection = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values };
      delete payload.nameLocales;
      const nl = values.nameLocales || {};
      payload.nameLocales = {
        'zh-TC': nl['zh-TC'] ?? '',
        'zh-SC': nl['zh-SC'] ?? ''
      };
      if (!isTenantAdmin) delete payload.companyId;
      setSaving(true);
      const isEdit = !!editingRecord;
      const url = isEdit ? `/api/commerce/collections/${editingRecord.id}` : '/api/commerce/collections';
      const method = isEdit ? 'PUT' : 'POST';
      const response = await fetchWithAuth(url, { method, body: JSON.stringify(payload) });
      if (!response.ok) throw new Error(await response.text());
      message.success(isEdit ? '集合已更新' : '集合已新增');
      setModalOpen(false);
      loadCollections();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(`保存失敗: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteCollection = async (id) => {
    try {
      const response = await fetchWithAuth(`/api/commerce/collections/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await response.text());
      message.success(t('commerceCollections.deleted'));
      loadCollections();
    } catch (err) {
      message.error(`${t('commerceCollections.deleteFailed')}: ${err.message}`);
    }
  };

  const syncCollection = async (record) => {
    try {
      setSyncingId(record.id);
      const response = await fetchWithAuth(`/api/commerce/sync/collection/${record.id}`, { method: 'POST' });
      if (!response.ok) throw new Error(await response.text());
      message.success(t('commerceCollections.syncSuccess'));
    } catch (err) {
      message.error(`${t('commerceCollections.syncFailed')}: ${err.message}`);
    } finally {
      setSyncingId(null);
    }
  };

  const batchSyncCollections = async () => {
    const ids = selectedRowKeys;
    if (!ids || ids.length === 0) {
      message.warning(t('commerceCollections.selectAtLeastOne'));
      return;
    }
    const withItems = ids.filter((id) => {
      const c = collections.find((x) => x.id === id);
      return c && (c.itemCount ?? 0) > 0;
    });
    const notOnPage = ids.filter((id) => !collections.some((x) => x.id === id));
    if (notOnPage.length > 0) {
      message.warning(
        t('commerceCollections.batchSyncOnlyCurrentPage') ||
          '跨頁選取的集合請切回該頁再批次同步，或取消選取其他頁的列。'
      );
    }
    const skipped = ids.length - withItems.length;
    if (withItems.length === 0) {
      message.warning(t('commerceCollections.syncEmptyHint'));
      return;
    }
    try {
      let ok = 0;
      for (const id of withItems) {
        const res = await fetchWithAuth(`/api/commerce/sync/collection/${id}`, { method: 'POST' });
        if (res.ok) ok += 1;
      }
      if (skipped > 0) {
        message.success(t('commerceCollections.batchUploadQueuedSkipped', { count: ok, total: withItems.length, skipped }));
      } else {
        message.success(t('commerceCollections.batchUploadQueued', { count: ok, total: ids.length }));
      }
      setSelectedRowKeys([]);
    } catch (err) {
      message.error(`${t('commerceCollections.syncFailed')}: ${err.message}`);
    }
  };

  const openLinkProducts = async (record) => {
    try {
      linkModalLastSearchFetched.current = null;
      setLinkProductSearchKeyword('');
      setLinkSearchDebounced('');
      setLinkModalPage(1);
      setLinkModalPageSize(COMMERCE_DEFAULT_PAGE_SIZE);
      setSelectedCollection(record);
      setLinkModalOpen(true);
      const response = await fetchWithAuth(`/api/commerce/collections/${record.id}/items`);
      if (!response.ok) throw new Error(await response.text());
      const items = await response.json();
      const products = Array.isArray(items) ? items : [];
      setLinkedProducts(products);
      setLinkedProductIds(products.map((x) => x.id).filter(Boolean));
    } catch (err) {
      message.error(`載入關聯商品失敗: ${err.message}`);
    }
  };

  const removeLinkedProduct = (productId) => {
    setLinkedProductIds((prev) => prev.filter((id) => id !== productId));
    setLinkedProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  const onAvailableProductsSelectionChange = (keys) => {
    const pageIds = new Set(linkModalProducts.map((p) => p.id));
    const otherLinked = linkedProductIds.filter((id) => !pageIds.has(id));
    setLinkedProductIds([...otherLinked, ...keys]);
    setLinkedProducts((prev) => {
      const byId = new Map(prev.map((p) => [p.id, p]));
      linkModalProducts.forEach((p) => {
        if (keys.includes(p.id)) byId.set(p.id, p);
        else if (pageIds.has(p.id)) byId.delete(p.id);
      });
      return [...byId.values()];
    });
  };

  const saveLinkedProducts = async () => {
    if (!selectedCollection) return;
    try {
      setSaving(true);
      const response = await fetchWithAuth(`/api/commerce/collections/${selectedCollection.id}/items:bulk-set`, {
        method: 'POST',
        body: JSON.stringify({ productIds: linkedProductIds })
      });
      if (!response.ok) throw new Error(await response.text());
      message.success(t('commerceCollections.linkSaved'));
      setLinkModalOpen(false);
      setLinkedProducts([]);
      setLinkedProductIds([]);
      linkModalLastSearchFetched.current = null;
      loadCollections();
    } catch (err) {
      message.error(`${t('commerceCollections.linkSaveFailed')}: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const companyIdToName = Object.fromEntries((companiesForSelect || []).map(c => [c.id, c.name]));

  const columns = useMemo(() => [
    {
      title: t('commerceCollections.nameColUiLocale'),
      key: 'displayNameUi',
      width: 260,
      ellipsis: true,
      render: (_, r) =>
        resolveLocalizedEntityName(r.name, r.nameLocales ?? r.name_locales, currentLanguage) || '—'
    },
    {
      title: t('commerceCollections.company'),
      dataIndex: 'companyId',
      key: 'companyId',
      width: 200,
      ellipsis: true,
      render: (id) => companyIdToName[id] || id || '-'
    },
    {
      title: t('commerceCollections.itemCount'),
      dataIndex: 'itemCount',
      key: 'itemCount',
      width: 88,
      align: 'center',
      render: (n, record) => <Tag style={{ margin: 0 }}>{n ?? record.itemCount ?? 0}</Tag>
    },
    ...buildCommerceAuditColumns(t, userTimezoneOffset),
    {
      title: t('commerceCollections.actions'),
      key: 'actions',
      width: 320,
      fixed: 'right',
      render: (_, record) => (
        <Space wrap={false} size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>{t('commerceCollections.edit')}</Button>
          <Button size="small" icon={<LinkOutlined />} onClick={() => openLinkProducts(record)}>{t('commerceCollections.linkProducts')}</Button>
          <Tooltip title={(record.itemCount ?? 0) === 0 ? t('commerceCollections.syncEmptyHint') : null}>
            <span>
              <Button
                size="small"
                icon={<SyncOutlined spin={syncingId === record.id} />}
                onClick={() => (record.itemCount ?? 0) > 0 && syncCollection(record)}
                loading={syncingId === record.id}
                disabled={(record.itemCount ?? 0) === 0}
              >
                {t('commerceCollections.sync')}
              </Button>
            </span>
          </Tooltip>
          <Popconfirm title={t('commerceCollections.confirmDelete')} onConfirm={() => deleteCollection(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>{t('commerceCollections.delete')}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ], [t, currentLanguage, companyIdToName, syncingId, userTimezoneOffset]);

  const linkModalProductColumns = useMemo(
    () => [
      {
        title: t('commerceProducts.image'),
        dataIndex: 'imageUrl',
        key: 'imageUrl',
        width: 72,
        fixed: 'left',
        render: (url) =>
          url ? (
            <img src={url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
          ) : (
            '-'
          )
      },
      {
        title: t('commerceProducts.nameColUiLocale'),
        key: 'displayName',
        width: 200,
        ellipsis: true,
        render: (_, r) =>
          resolveLocalizedEntityName(r.name, r.nameLocales ?? r.name_locales, currentLanguage) || '—'
      },
      {
        title: t('commerceProducts.description'),
        dataIndex: 'description',
        key: 'description',
        width: 200,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.link'),
        dataIndex: 'linkUrl',
        key: 'linkUrl',
        width: 160,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.company'),
        dataIndex: 'companyId',
        key: 'companyId',
        width: 140,
        ellipsis: true,
        render: (id) => companyIdToName[id] || id || '-'
      },
      {
        title: t('commerceProducts.contentId'),
        dataIndex: 'productRetailerId',
        key: 'productRetailerId',
        width: 150,
        ellipsis: true
      },
      {
        title: t('commerceProducts.fbCategory'),
        dataIndex: 'fbProductCategory',
        key: 'fbProductCategory',
        width: 200,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.brand'),
        dataIndex: 'brand',
        key: 'brand',
        width: 120,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.contentNumber'),
        dataIndex: 'customLabel0',
        key: 'customLabel0',
        width: 120,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.color'),
        dataIndex: 'color',
        key: 'color',
        width: 90,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.gender'),
        dataIndex: 'gender',
        key: 'gender',
        width: 100,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.material'),
        dataIndex: 'material',
        key: 'material',
        width: 100,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.pattern'),
        dataIndex: 'pattern',
        key: 'pattern',
        width: 100,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.size'),
        dataIndex: 'size',
        key: 'size',
        width: 90,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.unitOfMeasure'),
        dataIndex: 'unitOfMeasure',
        key: 'unitOfMeasure',
        width: 90,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.availability'),
        dataIndex: 'availability',
        key: 'availability',
        width: 120,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.condition'),
        dataIndex: 'condition',
        key: 'condition',
        width: 100,
        ellipsis: true,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.currency'),
        dataIndex: 'currency',
        key: 'currency',
        width: 80,
        render: (v) => v || '-'
      },
      {
        title: t('commerceProducts.price'),
        key: 'price',
        width: 120,
        render: (_, r) =>
          r.price == null || r.price === '' ? '-' : `${r.currency || 'HKD'} ${Number(r.price).toFixed(2)}`
      },
      {
        title: t('commerceProducts.status'),
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (v) => (
          <Tag color={String(v).toLowerCase() === 'active' ? 'green' : 'default'}>{v || '—'}</Tag>
        )
      }
    ],
    [t, currentLanguage, companyIdToName]
  );

  const filteredLinkedProducts = useMemo(() => {
    const q = linkSearchDebounced.trim().toLowerCase();
    if (!q) return linkedProducts;
    return linkedProducts.filter((p) => {
      const locName = (
        resolveLocalizedEntityName(p.name, p.nameLocales ?? p.name_locales, currentLanguage) || ''
      ).toLowerCase();
      const hay = [
        p.name,
        locName,
        p.description,
        p.productRetailerId,
        p.brand,
        p.fbProductCategory,
        p.customLabel0
      ]
        .filter((x) => x != null && String(x).trim() !== '')
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [linkedProducts, linkSearchDebounced, currentLanguage]);

  const linkModalLinkedColumns = useMemo(
    () => [
      ...linkModalProductColumns,
      {
        title: t('commerceCollections.actions'),
        key: 'unlink',
        width: 88,
        fixed: 'right',
        render: (_, record) => (
          <Button size="small" danger onClick={() => removeLinkedProduct(record.id)}>
            {t('commerceCollections.linkProductsRemove')}
          </Button>
        )
      }
    ],
    [linkModalProductColumns, t]
  );

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              {isTenantAdmin && (
                <Select
                  allowClear
                  placeholder={t('commerceCollections.filterByCompany')}
                  style={{ width: 220 }}
                  value={companyFilter}
                  onChange={(v) => {
                    setCompanyFilter(v);
                    setCollectionsPage(1);
                  }}
                  options={companiesForSelect.map(c => ({ label: c.name, value: c.id }))}
                />
              )}
              <Button
                icon={<SyncOutlined />}
                onClick={batchSyncCollections}
                disabled={!selectedRowKeys || selectedRowKeys.length === 0}
              >
                {t('commerceCollections.batchUploadToMeta')} {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={loadCollections}>{t('commerceCollections.refresh')}</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('commerceCollections.addCollection')}</Button>
            </Space>
            <div style={{ textAlign: 'right' }}>
              <Title level={3} style={{ margin: 0 }}>{t('commerceCollections.title')}</Title>
              <Text type="secondary">{t('commerceCollections.description')}</Text>
            </div>
          </div>

          <Table
            rowKey="id"
            loading={loading}
            tableLayout="fixed"
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys)
            }}
            columns={columns}
            dataSource={collections}
            pagination={commerceServerTablePagination({
              page: collectionsPage,
              pageSize: collectionsPageSize,
              total: collectionsTotal,
              onChange: (p, ps) => {
                setCollectionsPage(p);
                setCollectionsPageSize(ps);
              }
            })}
            scroll={{ x: 940, y: 'calc(100vh - 340px)' }}
          />
        </Space>
      </Card>

      <Modal
        open={modalOpen}
        title={editingRecord ? t('commerceCollections.editCollection') : t('commerceCollections.addCollectionModal')}
        onCancel={() => setModalOpen(false)}
        onOk={saveCollection}
        confirmLoading={saving}
        width={760}
      >
        <Form form={form} layout="vertical">
          {isTenantAdmin ? (
            <Form.Item name="companyId" label={t('commerceCollections.company')} rules={[{ required: true, message: t('commerceCatalogAdmin.required') }]}>
              <Select
                placeholder={t('commerceCollections.selectCompany')}
                options={companiesForSelect.map(c => ({ label: c.name, value: c.id }))}
                showSearch
                optionFilterProp="label"
                filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes((input || '').toLowerCase())}
              />
            </Form.Item>
          ) : (
            <Form.Item label={t('commerceCollections.company')}>
              <Input disabled value={companiesForSelect.find(c => c.id === userInfo.company_id)?.name || userInfo.company_id || '-'} />
            </Form.Item>
          )}
          <Form.Item name="name" label={t('commerceCollections.collectionName')} rules={[{ required: true, message: t('commerceCatalogAdmin.required') }]}>
            <Input />
          </Form.Item>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>{t('commerceCollections.nameLocalesHint')}</Text>
          <Row gutter={12}>
            {COMMERCE_NAME_LOCALE_EXTRA_KEYS.map(({ jsonKey, formPath }) => (
              <Col span={12} key={jsonKey}>
                <Form.Item
                  name={formPath}
                  label={(
                    <span>
                      <Tooltip title={jsonKey}>
                        <Tag>{jsonKey === 'zh-TC' ? '繁' : '简'}</Tag>
                      </Tooltip>
                      {jsonKey === 'zh-TC' ? t('commerceCollections.nameLocaleZhTc') : t('commerceCollections.nameLocaleZhSc')}
                    </span>
                  )}
                >
                  <Input />
                </Form.Item>
              </Col>
            ))}
          </Row>
          <Form.Item name="description" label={t('commerceCollections.description')}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="externalCollectionKey" label={t('commerceCollections.externalCollectionKey')}>
            <Input />
          </Form.Item>
          <Form.Item name="metaProductSetId" label={t('commerceCollections.metaProductSetId')}>
            <Input />
          </Form.Item>
          <Form.Item name="metaCatalogId" label={t('commerceCollections.metaCatalogId')}>
            {catalogOptionsLoading ? (
              <Input disabled placeholder={t('commerceCollections.loading')} />
            ) : catalogOptions.length === 1 ? (
              <Input
                disabled
                value={`${catalogOptions[0].label} (${catalogOptions[0].value})`}
                addonBefore={t('commerceCollections.linkedCatalogDefault')}
              />
            ) : catalogOptions.length === 0 ? (
              <Alert type="info" showIcon message={t('commerceCollections.noCatalogLinkedHint')} />
            ) : (
              <Select
                allowClear
                showSearch
                placeholder={t('commerceCollections.selectCatalog')}
                options={catalogOptions}
                notFoundContent={t('commerceCollections.noCatalog')}
                optionFilterProp="label"
                filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes((input || '').toLowerCase())}
              />
            )}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={linkModalOpen}
        title={t('commerceCollections.linkProductsTitle', {
          name:
            resolveLocalizedEntityName(
              selectedCollection?.name,
              selectedCollection?.nameLocales ?? selectedCollection?.name_locales,
              currentLanguage
            ) || selectedCollection?.name || ''
        })}
        onCancel={() => {
          setLinkModalOpen(false);
          setLinkProductSearchKeyword('');
          setLinkedProducts([]);
          setLinkedProductIds([]);
          linkModalLastSearchFetched.current = null;
        }}
        onOk={saveLinkedProducts}
        confirmLoading={saving}
        width="min(96vw, 1680px)"
        styles={{ body: { maxHeight: 'calc(100vh - 220px)', overflow: 'auto' } }}
      >
        <Text type="secondary">{t('commerceCollections.linkProductsHint')}</Text>
        <Space style={{ marginTop: 12, marginBottom: 12, width: '100%' }} wrap align="center">
          <Input.Search
            allowClear
            placeholder={t('commerceCollections.linkProductsSearchPlaceholder')}
            value={linkProductSearchKeyword}
            onChange={(e) => setLinkProductSearchKeyword(e.target.value)}
            style={{ width: 380, maxWidth: '100%' }}
          />
          <Text type="secondary">
            {t('commerceCollections.linkProductsSelectedCount').replace('{n}', String(linkedProductIds.length))}
          </Text>
        </Space>

        <Text strong>{t('commerceCollections.linkProductsLinkedSection')}</Text>
        <Table
          size="small"
          rowKey="id"
          style={{ marginTop: 8 }}
          columns={linkModalLinkedColumns}
          dataSource={filteredLinkedProducts}
          locale={{ emptyText: t('commerceCollections.linkProductsLinkedEmpty') }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total) => `${total}`
          }}
          scroll={{ x: 'max-content', y: 200 }}
        />

        <Divider style={{ margin: '16px 0' }} />

        <Text strong>{t('commerceCollections.linkProductsAvailableSection')}</Text>
        <Table
          size="small"
          rowKey="id"
          style={{ marginTop: 8 }}
          loading={linkModalLoading}
          columns={linkModalProductColumns}
          dataSource={linkModalProducts}
          rowSelection={{
            selectedRowKeys: linkModalProducts.filter((p) => linkedProductIds.includes(p.id)).map((p) => p.id),
            onChange: onAvailableProductsSelectionChange,
            preserveSelectedRowKeys: true
          }}
          pagination={commerceServerTablePagination({
            page: linkModalPage,
            pageSize: linkModalPageSize,
            total: linkModalTotal,
            onChange: (p, ps) => {
              setLinkModalPage(p);
              setLinkModalPageSize(ps);
            }
          })}
          scroll={{ x: 'max-content', y: 280 }}
        />
      </Modal>
    </div>
  );
};

export default CommerceCollectionsPage;

